import { createSlice } from "@reduxjs/toolkit";

const athleteSlice = createSlice({
  name: "athletes",
  initialState: {
    profilesById: {},
    athletes: [],
    favouriteAthletes: [],
    similarAthletes: [],
    profile: null,
    offers: [],
    accolades: [],
    loading: false,
    error: null,
    config: {
      showOffers: true,
      showTrackittScore: true,
      showPositionScore: true,
      searchEvent: false,
      eventId: undefined,
      showProfile: true,
      showRank: true,
      showLikes: true,
    },
    buttons: {
      combine: {
        tab: true,
        athleteTabSelected: true,
        positionTabSelected: false,
        positionYearTabSelected: false,
      },
      measurements: {
        tab: true,
        athleteTabSelected: true,
        positionTabSelected: false,
        positionYearTabSelected: false,
      },
    },
    positionDrill: [],
    reload: false,
    refreshMenuKey: 0,
  },
  reducers: {
    fetchAthleteProfileRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAthleteProfileSuccess: (state, action) => {
      const profile = action.payload;
      state.loading = false;
      state.profile = profile;
      state.profilesById[profile.id] = profile;
    },
    fetchAthleteProfileFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchOffersSuccess: (state, action) => {
      state.offers = action.payload;
    },
    fetchAccoladesSuccess: (state, action) => {
      state.accolades = action.payload;
    },
    fetchProfilePhotoRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchProfilePhotoSuccess: (state, action) => {
      state.loading = false;
      state.profilePhoto = action.payload;
    },
    fetchProfilePhotoFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    removeAccolade: (state, action) => {
      const result = state.accolades.filter(
        (item) => item.id !== action.payload,
      );
      state.accolades = result;
    },
    removeOffer: (state, action) => {
      const result = state.offers.filter((item) => item.id !== action.payload);
      state.offers = result;
    },
    setProfile: (state, action) => {
      state.profile = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setScoreConfig: (state, action) => {
      state.config.showOffers = action.payload.showOffers;
      state.config.showTrackittScore = action.payload.showTrackittScore;
      state.config.showPositionScore = action.payload.showPositionScore;
      state.config.searchEvent = action.payload.searchEvent;
      state.config.showProfile = action.payload.showProfile;
      state.config.showRank = action.payload.showRank;
      state.config.showLikes = action.payload.showLikes;
      state.config.eventId = action.payload.eventId;
    },
    setEventId: (state, action) => {
      state.config.eventId = action.payload;
    },
    setButtonsCombine: (state, action) => {
      state.buttons.combine.athleteTabSelected = !!action.payload.athlete;
      state.buttons.combine.positionTabSelected = !!action.payload.position;
      state.buttons.combine.positionYearTabSelected = !!(
        action.payload.positionYear ?? action.payload.positionAge
      );
      state.buttons.measurements.athleteTabSelected = !!action.payload.athlete;
      state.buttons.measurements.positionTabSelected =
        !!action.payload.position;
      state.buttons.measurements.positionYearTabSelected = !!(
        action.payload.positionYear ?? action.payload.positionAge
      );
    },
    setShowTab: (state, action) => {
      state.buttons.combine.tab = action.payload.combine;
      state.buttons.measurements.tab = action.payload.measurement;
    },
    setPositionDrill: (state, action) => {
      state.positionDrill = action.payload;
    },
    setReload: (state, action) => {
      state.reload = action.payload;
    },
    setAthletes: (state, action) => {
      state.athletes = action.payload;
    },
    bumpMenuRefresh(state) {
      state.refreshMenuKey += 1;
    },
    setFavouriteAthletes: (state, action) => {
      state.favouriteAthletes = action.payload;
    },
    setSimilarAthletes: (state, action) => {
      state.similarAthletes = action.payload;
    },
    mergeAthleteFavouriteStatuses: (state, action) => {
      action.payload.forEach(({ userId, liked }) => {
        if (userId == null) return;
        const existing = state.favouriteAthletes.find(
          (a) => a.userId === userId,
        );
        if (existing) {
          existing.liked = liked;
        } else {
          state.favouriteAthletes.push({ userId, liked });
        }
      });
    },
    toggleFavouriteLocal: (state, action) => {
      const { athleteUserId, liked } = action.payload;

      const fav = state.favouriteAthletes?.find(
        (a) => a.userId === athleteUserId,
      );
      if (fav) {
        fav.liked = liked;
      } else {
        state.favouriteAthletes.push({ userId: athleteUserId, liked });
      }

      if (state.profile?.id === athleteUserId) {
        state.profile.likes = Math.max(
          0,
          (state.profile.likes || 0) + (liked ? 1 : -1),
        );
      }
    },
  },
});

export const {
  fetchAthleteProfileRequest,
  fetchAthleteProfileSuccess,
  fetchAthleteProfileFail,
  fetchOffersSuccess,
  fetchAccoladesSuccess,
  fetchProfilePhotoRequest,
  fetchProfilePhotoSuccess,
  fetchProfilePhotoFail,
  removeAccolade,
  removeOffer,
  setProfile,
  setLoading,
  setScoreConfig,
  setEventId,
  setButtonsCombine,
  setShowTab,
  setPositionDrill,
  setReload,
  setAthletes,
  bumpMenuRefresh,
  setFavouriteAthletes,
  setSimilarAthletes,
  mergeAthleteFavouriteStatuses,
  toggleFavouriteLocal,
} = athleteSlice.actions;

export default athleteSlice.reducer;
